import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { IgLink, IgTypography } from "@intelligems/ig-theme";
import Stack from "@mui/material/Stack";
import { experienceKeys } from "@src/api/queryKeys";
import { EXPERIENCE_ROUTES } from "@src/routes/routes";
import { useQueryClient } from "@tanstack/react-query";

interface DiscountSyncErrorToastProps {
  onClick: (ev: React.MouseEvent) => void;
}

// react-toastify renders toast content in its own container, which lives
// outside the <Router> tree — so this component must not call router hooks like
// `useNavigate()` (it throws "useNavigate() may be used only in the context of a
// <Router> component"). Navigation is handled by the caller instead.
// eslint-disable-next-line react-refresh/only-export-components
export const DiscountSyncErrorToast = ({
  onClick,
}: DiscountSyncErrorToastProps) => {
  return (
    <Stack direction={"column"} onClick={onClick}>
      <IgTypography fontWeight={600}>
        Discount Synchronization Error
      </IgTypography>
      <IgTypography>
        Go to <IgLink>Merchandising</IgLink> to correct the error.
      </IgTypography>
    </Stack>
  );
};

export function useDiscountSyncErrorToast() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const ref = useRef<"success" | "error">(undefined);

  return (status: "success" | "error") => {
    if (status === ref.current) {
      return;
    }
    ref.current = status;

    if (status === "error") {
      const onClick = () => {
        void navigate(EXPERIENCE_ROUTES.Experiences_Merchandising);
        toast.dismiss("DiscountSyncError");
      };
      toast.error(<DiscountSyncErrorToast onClick={onClick} />, {
        toastId: "DiscountSyncError",
        autoClose: 20000,
        closeOnClick: false,
      });
    } else {
      toast.info("Discount Synchronization Error Resolved");
    }

    void queryClient.invalidateQueries({
      queryKey: experienceKeys.homeOverviewPersonalizationList,
    });
  };
}
